import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartReportResponse, ChartType } from 'shared/types/asset-report';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, LineElement,
  PointElement, ArcElement, Title, Tooltip, Legend, Filler,
);

const CHART_COLORS = [
  '#EA8162', '#5B7304', '#31403B', '#F59E0B', '#3B82F6',
  '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#6366F1',
  '#84CC16', '#06B6D4', '#A855F7', '#F43F5E', '#22C55E',
];

interface ChartViewProps {
  data: ChartReportResponse;
  chartType: ChartType;
  title?: string;
  sortOrder?: 'none' | 'asc' | 'desc';
}

const ChartView: React.FC<ChartViewProps> = ({ data, chartType, title, sortOrder = 'none' }) => {
  const isPieType = chartType === 'pie' || chartType === 'doughnut';
  const isStacked = chartType === 'stacked_bar';
  const effectiveChartType = isStacked ? 'bar' : chartType;

  // Sort data by the first dataset's values (or total of all datasets for stacked)
  const sortedData = useMemo(() => {
    if (!data?.labels?.length || !data?.datasets?.length) return null;
    if (sortOrder === 'none') return data;

    const totals = data.labels.map((_, i) =>
      data.datasets.reduce((sum, ds) => sum + (ds.data[i] || 0), 0)
    );
    const indices = data.labels.map((_, i) => i);
    indices.sort((a, b) =>
      sortOrder === 'asc' ? totals[a] - totals[b] : totals[b] - totals[a]
    );
    return {
      labels: indices.map(i => data.labels[i]),
      datasets: data.datasets.map(ds => ({
        ...ds,
        data: indices.map(i => ds.data[i]),
      })),
    };
  }, [data, sortOrder]);

  // Safe reference — never null, avoids conditional hooks
  const safeData = sortedData || { labels: [] as string[], datasets: [] as { label: string; data: number[] }[] };

  const chartData = useMemo(() => {
    if (isPieType) {
      // Pie/Doughnut: single dataset with colors per segment
      const dataset = safeData.datasets[0] || { label: '', data: [] };
      return {
        labels: safeData.labels,
        datasets: [{
          label: dataset.label,
          data: dataset.data,
          backgroundColor: CHART_COLORS.slice(0, safeData.labels.length),
          borderColor: '#ffffff',
          borderWidth: 2,
        }],
      };
    }

    // Bar/Line/Stacked: multiple datasets
    return {
      labels: safeData.labels,
      datasets: safeData.datasets.map((ds, i) => ({
        label: ds.label,
        data: ds.data,
        backgroundColor: CHART_COLORS[i % CHART_COLORS.length] + (effectiveChartType === 'line' ? '33' : ''),
        borderColor: CHART_COLORS[i % CHART_COLORS.length],
        borderWidth: effectiveChartType === 'line' ? 2 : (isStacked ? 1 : 0),
        fill: effectiveChartType === 'line',
        tension: 0.3,
        pointRadius: effectiveChartType === 'line' ? 3 : 0,
      })),
    };
  }, [safeData, chartType, isPieType, effectiveChartType, isStacked]);

  const options: any = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: isPieType ? 'right' as const : 'top' as const,
        labels: { usePointStyle: true, padding: 16, font: { size: 12 } },
      },
      tooltip: {
        backgroundColor: '#31403B',
        titleColor: '#F9F0EE',
        bodyColor: '#F9F0EE',
        borderColor: '#EA8162',
        borderWidth: 1,
        padding: 12,
        callbacks: {
          label: (context: any) => {
            const value = context.parsed?.y ?? context.parsed ?? context.raw;
            const formatted = typeof value === 'number'
              ? new Intl.NumberFormat('nl-BE', { maximumFractionDigits: 2 }).format(value)
              : value;
            return `${context.dataset.label || ''}: ${formatted}`;
          },
        },
      },
    },
    ...(isPieType ? {} : {
      scales: {
        x: {
          stacked: isStacked,
          grid: { display: false },
          ticks: { font: { size: 11 }, maxRotation: 45 },
        },
        y: {
          stacked: isStacked,
          beginAtZero: true,
          grid: { color: '#f0f0f0' },
          ticks: {
            font: { size: 11 },
            callback: (value: number) =>
              new Intl.NumberFormat('nl-BE', { maximumFractionDigits: 0 }).format(value),
          },
        },
      },
    }),
  }), [isPieType]);

  const ChartComponent = {
    bar: Bar,
    stacked_bar: Bar,
    line: Line,
    pie: Pie,
    doughnut: Doughnut,
  }[chartType] || Bar;

  if (!safeData.labels.length) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-[400px]">
          <p className="text-muted-foreground text-sm">Geen data gevonden voor deze configuratie</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      {title && (
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">{title}</CardTitle>
        </CardHeader>
      )}
      <CardContent>
        <div className="h-[400px]">
          <ChartComponent data={chartData} options={options} />
        </div>
      </CardContent>
    </Card>
  );
};

export default ChartView;
